import { unstable_cache } from 'next/cache'
import { algoliasearch } from 'algoliasearch'
import { FacetConfiguration } from 'components/commercetools-ui/organisms/product/product-list-algolia/types'
import configLoader from 'config/helper'
import {
  buildAlgoliaFacetsConfigurationIndex,
  type AlgoliaFacetsConfigurationIndex,
} from 'helpers/algolia/buildAlgoliaFacetsConfigurationIndex'
import { mapContentstackFacetsConfiguration } from 'helpers/algolia/mapContentstackFacetsConfiguration'
import { stripContentstackMetadata } from 'helpers/contentstack/stripContentstackMetadata'
import {
  getAllEntriesByType,
  getEntry,
  getEntryByCategoryId,
  getEntryByCategoryKey,
  getEntryByProductKey,
  getEntryBySeoSlug,
  getEntryBySlug,
  getEntryByTitle,
} from 'helpers/utils/contentstack'
import { getIndex } from 'providers/algolia/localized-index/indexMap'
import { Category } from 'types/entity/category'

export type ContentstackPageFromAlgolia = ContentstackPage & { publish_details: { locale: string } }
export type ContentstackBrandFromAlgolia = ContentstackBrand & { publish_details: { locale: string } }
export type ContentstackPdpFromAlgolia = ContentstackPdp & { publish_details: { locale: string } }
export type ContentstackPlpFromAlgolia = ContentstackPlp & { publish_details: { locale: string } }

export type PdpBrandSsrEntry = { url: string } & Record<string, unknown>
export type PdpBrandSsrPayload = {
  brandLabel: string
  brandEntry?: PdpBrandSsrEntry
}

const CACHE_TAGS = ['contentstack']
const REVALIDATE = 300
const REVALIDATE_RELATED_AGOILIA_ENTITY = 86400 // 1 Tag

const BASE_REFERENCE_FIELD_PATH = [
  'content.gridsliderelement.grid_slider_content.brand_tile.brand_reference',
  'content.gridsliderelement.grid_slider_content.category_tile.brands',
  'content.gridsliderelement.grid_slider_content.page.page_ref',
  'content.contacts.contacts_content.contact_column.contacts',
  'desktop_file',
  'content.form.form_type',
]

const buildEntryConfig = (locale: string, deAtFallback = false) => ({
  referenceFieldPath: BASE_REFERENCE_FIELD_PATH,
  jsonRtePath: [] as string[],
  locale: deAtFallback && locale !== 'cs-CZ' ? '' : locale,
})

const cachedFetchRelatedByUid = unstable_cache(
  async (uid: string, locale: string): Promise<unknown[]> => {
    const algoliaClient = algoliasearch(
      configLoader('algoliaAppId.id') as string,
      process.env.ALGOLIA_SEARCH_PARENT_API_KEY as string,
    )
    const indexName = getIndex('zooroyal-contentstack', locale as 'de-DE' | 'de-AT' | 'cs-CZ')
    const result = await algoliaClient.search([{ indexName, params: { filters: `uid:${uid}` } }])
    return (result.results?.[0] as Record<string, unknown>)?.hits as unknown[] ?? []
  },
  ['cs-related-by-uid'],
  { revalidate: REVALIDATE_RELATED_AGOILIA_ENTITY, tags: CACHE_TAGS },
)

const withRelated = async (entryData: unknown, locale: string) => {
  if (!entryData) return false
  const normalized = (Array.isArray(entryData) ? entryData[0] : entryData) as { uid: string }
  const related = await cachedFetchRelatedByUid(normalized.uid, locale)
  // Strip system fields from the CS API entry. JSON.parse/stringify preserves the `any`
  // inferred type that call sites need for `as ContentstackPage | ContentstackBrand` casts.
  // related items come from Algolia (already lean) and are consumed server-side only
  // (metadata.ts reads related[n].publish_details.locale for hreflang) — leave untouched.
  return {
    ...stripContentstackMetadata(JSON.parse(JSON.stringify(normalized))),
    related,
  }
}

const cachedFetchPageEntry = unstable_cache(
  async (slugPath: string, locale: string) => {
    if (!slugPath) return false
    const { data } = await getEntryBySlug({ contentTypeUid: 'page', slug: slugPath.split('/'), ...buildEntryConfig(locale) })
    return withRelated(data?.[0], locale)
  },
  ['cs-page'],
  { revalidate: REVALIDATE, tags: CACHE_TAGS },
)

const cachedFetchBrandEntry = unstable_cache(
  async (slugPath: string, locale: string) => {
    if (!slugPath) return false
    const { data } = await getEntryBySlug({ contentTypeUid: 'brand', slug: slugPath.split('/'), ...buildEntryConfig(locale) })
    return withRelated(data?.[0], locale)
  },
  ['cs-brand'],
  { revalidate: REVALIDATE, tags: CACHE_TAGS },
)

const cachedFetchPlpEntry = unstable_cache(
  async (slugPath: string, locale: string, deAtFallback: boolean) => {
    if (!slugPath) return false
    const { data } = await getEntryBySeoSlug({ contentTypeUid: 'plp', slug: slugPath.split('/'), ...buildEntryConfig(locale, deAtFallback) })
    return withRelated(data?.[0], locale)
  },
  ['cs-plp'],
  { revalidate: REVALIDATE, tags: CACHE_TAGS },
)

const cachedFetchPdpEntry = unstable_cache(
  async (productKey: string, locale: string) => {
    const { data } = await getEntryByProductKey({ contentTypeUid: 'pdp', productKey, ...buildEntryConfig(locale) })
    return withRelated(data?.[0], locale)
  },
  ['cs-pdp'],
  { revalidate: REVALIDATE, tags: CACHE_TAGS },
)

const FACETS_CONFIGURATION_REFERENCE_FIELDS = [
  ...BASE_REFERENCE_FIELD_PATH,
  'facets.facet.custom_facet_values',
]

const cachedFetchAllAlgoliaFacetsConfigurationEntries = unstable_cache(
  async (locale: string): Promise<ContentstackAlgoliaFacetsConfiguration[]> => {
    const result = await getAllEntriesByType({
      contentTypeUid: 'algolia_facets_configuration',
      locale,
      fetchAll: true,
      referenceFieldPath: FACETS_CONFIGURATION_REFERENCE_FIELDS,
    })
    return (result.data ?? []) as unknown as ContentstackAlgoliaFacetsConfiguration[]
  },
  ['cs-algolia-facets-entries'],
  { revalidate: REVALIDATE, tags: CACHE_TAGS },
)

export const getCachedAlgoliaFacetsConfigurationIndex = unstable_cache(
  async (locale: string, currency: string): Promise<AlgoliaFacetsConfigurationIndex> => {
    const entries = await cachedFetchAllAlgoliaFacetsConfigurationEntries(locale)
    return buildAlgoliaFacetsConfigurationIndex(
      entries.map((entry) => ({
        title: entry.title,
        main_category: entry.main_category,
        facetsConfiguration: mapContentstackFacetsConfiguration(entry.facets, locale, currency),
      })),
    )
  },
  ['cs-algolia-facets-index'],
  { revalidate: REVALIDATE, tags: CACHE_TAGS },
)

export const getCachedContentstackPageOrBrandData = (slug: string[], locale: string, contentType: 'page' | 'brand') =>
  contentType === 'page'
    ? cachedFetchPageEntry(slug.join('/'), locale)
    : cachedFetchBrandEntry(slug.join('/'), locale)

export const getCachedContentstackBrandData = (slug: string[], locale: string) =>
  cachedFetchBrandEntry(slug.join('/'), locale)

export const getCachedContentstackPDPData = (productKey: string, locale: string) =>
  cachedFetchPdpEntry(productKey, locale)

export const getCachedPdpProductKeySet = unstable_cache(
  async (locale: string): Promise<Record<string, true>> => {
    const result = await getAllEntriesByType({
      contentTypeUid: 'pdp',
      locale,
      fetchAll: true,
      excludeFields: ['content', 'description', 'additional_description'],
    })
    return (result.data ?? []).reduce<Record<string, true>>((acc, entry) => {
      const pdpEntry = entry as unknown as Record<string, unknown>
      const productid = pdpEntry?.productid as Record<string, unknown> | undefined
      const data = productid?.data
      const key = Array.isArray(data)
        ? ((data[0] as Record<string, unknown>)?.key as string | undefined)
        : ((data as Record<string, unknown> | undefined)?.key as string | undefined)
      if (typeof key === 'string' && key.length > 0) acc[key] = true
      return acc
    }, {})
  },
  ['cs-pdp-productkey-set'],
  { revalidate: REVALIDATE, tags: CACHE_TAGS },
)

export const getCachedContentstackPLPDataBySlug = (slug: string[], locale: string, deAtFallback = false) =>
  cachedFetchPlpEntry(slug.join('/'), locale, deAtFallback)

export const getCachedPlpSeoUrlMap = unstable_cache(
  async (locale: string): Promise<Record<string, string>> => {
    const result = await getAllEntriesByType({
      contentTypeUid: 'plp',
      locale,
      fetchAll: true,
      excludeFields: ['content'],
    })
    return (result.data ?? []).reduce<Record<string, string>>((acc, entry) => {
      const plpEntry = entry as unknown as Record<string, unknown>
      const seoUrl = plpEntry?.seo_url as string | undefined
      const filterQuery = plpEntry?.filter_string_query as string | undefined
      if (typeof seoUrl === 'string' && seoUrl.length > 0 && typeof filterQuery === 'string' && filterQuery.length > 0) {
        acc[seoUrl] = filterQuery
      }
      return acc
    }, {})
  },
  ['cs-plp-seo-url-map'],
  { revalidate: REVALIDATE, tags: CACHE_TAGS },
)

type ContentstackUrlEntry = { url?: string }
export type ContentstackUrlMap = Record<string, 'page' | 'brand'>

export const getCachedPageAndBrandUrls = unstable_cache(
  async (locale: string): Promise<ContentstackUrlMap> => {
    const [pageResult, brandResult] = await Promise.all([
      getAllEntriesByType({ contentTypeUid: 'page', locale, fetchAll: true, excludeFields: ['content'] }),
      getAllEntriesByType({ contentTypeUid: 'brand', locale, fetchAll: true, excludeFields: ['content'] }),
    ])

    const toMap = (entries: ContentstackUrlEntry[], type: 'page' | 'brand'): ContentstackUrlMap =>
      entries.reduce<ContentstackUrlMap>((acc, entry) => {
        if (typeof entry.url === 'string' && entry.url.length > 1) {
          acc[entry.url] = type
        }
        return acc
      }, {})

    return {
      ...toMap((pageResult.data ?? []) as ContentstackUrlEntry[], 'page'),
      ...toMap((brandResult.data ?? []) as ContentstackUrlEntry[], 'brand'),
    }
  },
  ['cs-page-and-brand-urls'],
  { revalidate: REVALIDATE, tags: CACHE_TAGS },
)

/** Title-based lookup (e.g. `PLP`, `Search Result`) against the cached facets configuration index. */
export const getCachedContentstackAlgoliaFacetsConfiguration = async (
  title: string,
  locale: string,
  currency: string,
): Promise<Record<string, FacetConfiguration>> => {
  const index = await getCachedAlgoliaFacetsConfigurationIndex(locale, currency)
  return index.byTitle[title] ?? {}
}

export const getCachedContentstackPLP = unstable_cache(
  async (locale: string, category?: Category): Promise<ContentstackPlp | undefined> => {
    if (!category || !category.categoryKey) return undefined
    try {
      const fetchedContentstackData = await getEntryByCategoryKey({
        categoryKey: category.categoryKey,
        contentTypeUid: 'plp',
        locale,
        referenceFieldPath: [
          'content.brandlist.brands',
          'content.gridsliderelement.grid_slider_content.brand_tile.brand_reference',
          'content.gridsliderelement.grid_slider_content.category_tile.brands',
          'content.gridsliderelement.grid_slider_content.page.page_ref',
          'content.contacts.contacts_content.contact_column.contacts',
          'content.form.form_type',
        ],
      })
      const entry = Array.isArray(fetchedContentstackData.data)
        ? fetchedContentstackData.data[0]
        : undefined
      return entry ? (stripContentstackMetadata(entry) as ContentstackPlp) : undefined
    } catch {
      return undefined
    }
  },
  ['cs-plp-by-category'],
  { revalidate: REVALIDATE, tags: CACHE_TAGS },
)

export const loadCachedPdpBrandEntryByLabel = unstable_cache(
  async (locale: string, brandLabel: string): Promise<PdpBrandSsrPayload | undefined> => {
    const { data } = await getEntryByTitle({ contentTypeUid: 'brand', title: brandLabel, referenceFieldPath: [], jsonRtePath: [], locale })
    const entry = data?.[0]
    const brandEntry = entry && typeof entry === 'object' && 'url' in entry ? entry as unknown as PdpBrandSsrEntry : undefined
    return { brandLabel, brandEntry }
  },
  ['cs-pdp-brand-by-label'],
  { revalidate: REVALIDATE, tags: CACHE_TAGS },
)

export const getCachedEntryByTitle = unstable_cache(
  async (
    title: string,
    contentTypeUid: string,
    referenceFieldPath: string[],
    jsonRtePath: string[],
    locale: string,
  ) => getEntryByTitle({ title, contentTypeUid, referenceFieldPath, jsonRtePath, locale }),
  ['cs-entry-by-title'],
  { revalidate: REVALIDATE, tags: CACHE_TAGS },
)

export const getCachedEntryByCategoryId = unstable_cache(
  async (categoryId: string, contentTypeUid: string, locale: string) =>
    getEntryByCategoryId({ categoryId, contentTypeUid, locale }),
  ['cs-entry-by-category-id'],
  { revalidate: REVALIDATE, tags: CACHE_TAGS },
)

export const getCachedAccountContentstackData = unstable_cache(
  async (locale: string) => getEntry({
    contentTypeUid: 'account',
    referenceFieldPath: undefined,
    jsonRtePath: undefined,
    queryBuilder: (query) => {
      query.ascending('uid')
      return query
    },
    locale,
  }),
  ['cs-account-data'],
  { revalidate: REVALIDATE, tags: CACHE_TAGS },
)

const cachedFetchGroundingPageEntry = unstable_cache(
  async (locale: string): Promise<ContentstackGroundingPage | null> => {
    // Singleton entity page with structured fields only — no reference expansion needed.
    const { data } = await getEntry({
      contentTypeUid: 'grounding_page',
      referenceFieldPath: [],
      jsonRtePath: [],
      queryBuilder: (query) => query.ascending('uid'),
      locale,
    })

    return (data?.[0] as ContentstackGroundingPage) ?? null
  },
  ['cs-grounding-page'],
  { revalidate: REVALIDATE, tags: CACHE_TAGS },
)

export const getCachedGroundingPageData = (locale: string) =>
  cachedFetchGroundingPageEntry(locale)
